import { memo, type ReactNode } from 'react';

import { type WorkspaceMemberResponse } from '@air/api/types';
import { Avatar } from '@air/primitive-avatar';
import { cn } from '@air/utils-tailwind';

import { getAvatarImageSrc } from '~/utils/getAvatarImageSrc';

export type MemberInfoProps = {
  className?: string;
  member: Pick<WorkspaceMemberResponse, 'firstName' | 'lastName' | 'avatar' | 'email' | 'accountId'> & {
    inactive?: boolean;
    pending?: boolean;
  };
  nameAdornment?: ReactNode;
};

const MEMBER_AVATAR_SIZE_PX = 48;

export const MemberInfo = memo(
  ({
    className,
    member: { avatar, firstName, lastName, email, accountId, pending, inactive },
    nameAdornment,
  }: MemberInfoProps) => {
    const isPending = !!pending;
    const isInactive = !!inactive;

    return (
      <div className={cn('flex', className)}>
        <Avatar
          alt={`${firstName ?? ''} ${lastName ?? ''} avatar`}
          appearance="circle"
          className="shrink-0"
          size={MEMBER_AVATAR_SIZE_PX}
          src={getAvatarImageSrc(avatar, MEMBER_AVATAR_SIZE_PX)}
          text={isPending ? '' : `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`}
          colorSeed={accountId}
        />
        <div className="ml-4 overflow-x-hidden">
          <div className="flex items-center">
            <div
              className={cn(
                'truncate font-medium text-16',
                isInactive ? 'ads:text-metadata text-gray-10' : 'ads:text-primary text-gray-12',
              )}
            >
              {isPending ? email : `${firstName ?? ''} ${lastName ?? ''}`}
            </div>
            {nameAdornment}
          </div>

          <div
            className={cn(
              'truncate text-14',
              isInactive ? 'ads:text-metadata text-gray-8' : 'ads:text-metadata text-gray-10',
              isPending && 'italic',
            )}
          >
            {isPending ? 'Pending...' : email}
          </div>
        </div>
      </div>
    );
  },
);

MemberInfo.displayName = 'MemberInfo';
